import React, { useRef, useState } from "react";
import { Button, Dropdown, Input, Modal } from "../../../BP2Components";
import IntlMessages from "../../../../util/IntlMessages";
import { useIntl } from "react-intl";

interface Parameter {
   amount?: "Int";
   url?: string;
}

interface BusinessMethod {
   label: string;
   name: string;
   parameters: Parameter;
   requirements?: { name: string; field: string }[];
}

interface Props {
   formActions: BusinessMethod[];
   handleSave: (
      businessMethodsOverride: Array<{ name: string; args: Record<string, any> }>,
      requiredFields: string[],
   ) => void;
   requirementsByName: Record<string, string[]>;
   isRecordEditing: boolean;
   disabled?: boolean;
   isFormInModal?: boolean;
}

const BusinessMethodsDropdown: React.FC<Props> = ({
   formActions,
   handleSave,
   requirementsByName,
   isRecordEditing,
   disabled,
   isFormInModal,
}) => {
   const [isOpen, setIsOpen] = useState(false);
   const [pendingMethod, setPendingMethod] = useState<BusinessMethod | null>(null);
   const [amountInput, setAmountInput] = useState("");
   const [urlInput, setUrlInput] = useState("");
   const triggerRef = useRef<HTMLSpanElement>(null);
   const intl = useIntl();

   if (!formActions?.length) return null;

   const isDisabled = isRecordEditing || !!disabled;
   const isSingleAction = formActions.length === 1;

   const execute = (method: BusinessMethod, args: Record<string, any> = {}) => {
      handleSave([{ name: method.name, args }], requirementsByName[method.name] ?? []);
   };

   const handleMethodSelect = (method: BusinessMethod) => {
      const requiresAmount = method.parameters?.amount === "Int";
      const hasUrl = !!method.parameters?.url;

      if (requiresAmount || hasUrl) {
         setAmountInput("");
         setUrlInput(method.parameters?.url ?? "");
         setPendingMethod(method);
      } else {
         execute(method);
      }
   };

   const handleModalConfirm = () => {
      if (!pendingMethod) return;
      const args: Record<string, any> = {};
      if (pendingMethod.parameters?.url !== undefined) args.url = urlInput;
      if (pendingMethod.parameters?.amount === "Int") args.amount = parseInt(amountInput, 10);
      execute(pendingMethod, args);
      setPendingMethod(null);
   };

   const isConfirmDisabled =
      (pendingMethod?.parameters?.amount === "Int" &&
         (amountInput === "" || Number.isNaN(parseInt(amountInput, 10)))) ||
      (pendingMethod?.parameters?.url !== undefined && urlInput === "");

   const items = isSingleAction
      ? []
      : formActions.map((method) => ({
           id: method.name,
           name: method.label,
           onClick: () => handleMethodSelect(method),
           disabled: isDisabled,
        }));

   return (
      <>
         {isSingleAction ? (
            <Button
               buttonType="primary"
               onClick={() => handleMethodSelect(formActions[0])}
               disabled={isDisabled}
            >
               {formActions[0].label}
            </Button>
         ) : (
            <span ref={triggerRef} style={{ display: "contents" }}>
               <Button
                  buttonType="primary"
                  onClick={() => setIsOpen((prev) => !prev)}
                  disabled={isDisabled}
               >
                  <IntlMessages id="form.executeAction" defaultMessage="Wykonaj akcje" />
                  <Dropdown
                     isOpen={isOpen}
                     items={items}
                     setIsOpen={setIsOpen}
                     ignoreRef={triggerRef}
                     checkForModalRoot={false}
                     customZIndex={isFormInModal ? 1100 : undefined}
                  />
               </Button>
            </span>
         )}

         <Modal
            isOpen={!!pendingMethod}
            onOk={handleModalConfirm}
            onOkDisabled={isConfirmDisabled}
            onOkText={intl.formatMessage({ id: "global.confirm", defaultMessage: "Potwierdź" })}
            onCancel={() => setPendingMethod(null)}
         >
            {pendingMethod && (
               <div
                  style={{
                     display: "flex",
                     flexDirection: "column",
                     gap: "12px",
                     padding: "8px 0",
                  }}
               >
                  <strong>{pendingMethod.label}</strong>
                  {pendingMethod.parameters?.url !== undefined && (
                     <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <label style={{ fontSize: "12px" }}>
                           <IntlMessages id="form.businessMethods.url" defaultMessage="URL" />
                        </label>
                        <Input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} />
                     </div>
                  )}
                  {pendingMethod.parameters?.amount === "Int" && (
                     <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <label style={{ fontSize: "12px" }}>
                           <IntlMessages id="form.enterVoucherQuantity" />
                        </label>
                        <Input
                           type="number"
                           value={amountInput}
                           onChange={(e) => setAmountInput(e.target.value)}
                        />
                     </div>
                  )}
               </div>
            )}
         </Modal>
      </>
   );
};

export default BusinessMethodsDropdown;
